import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, AlertTriangle, Loader2, RefreshCw, ExternalLink, User, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const HOUR_RANGES = [
  { v: 1,   label: "Dernière heure" },
  { v: 24,  label: "24 heures" },
  { v: 72,  label: "3 jours" },
  { v: 168, label: "7 jours" },
];

export default function AdminFrontendErrors() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hours, setHours] = useState(72);
  const [selected, setSelected] = useState(null);

  const load = async (h = hours) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/frontend/errors?hours=${h}&limit=200`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Accès refusé");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(hours); /* eslint-disable-line */ }, [hours]);

  if (loading && !data) {
    return (
      <div className="min-h-screen grid place-items-center">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
      </div>
    );
  }

  const total = data?.total || 0;
  const top = data?.top || [];
  const items = data?.items || [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50/40 to-white pb-20">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white/95 backdrop-blur-sm border-b border-rose-100">
        <div className="max-w-5xl mx-auto px-5 py-3 flex items-center gap-3">
          <Link
            to="/prosperrboss"
            className="w-10 h-10 rounded-full border border-[var(--border)] grid place-items-center active:scale-95"
            data-testid="back-to-admin"
          >
            <ChevronLeft className="w-4 h-4" />
          </Link>
          <div className="flex-1">
            <p className="text-[10px] font-bold uppercase tracking-widest text-rose-400">Monitoring</p>
            <p className="font-display font-bold text-base text-[var(--text)]">Erreurs frontend</p>
          </div>
          <button
            onClick={() => load(hours)}
            className="w-10 h-10 rounded-full border border-[var(--border)] grid place-items-center active:scale-95"
            data-testid="refresh-errors"
            aria-label="Rafraîchir"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 pt-6 space-y-5">
        {/* Range selector + big count */}
        <div className="rounded-3xl border-2 border-rose-200 bg-rose-500/10/60 p-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-rose-500/100 grid place-items-center shrink-0">
              <AlertTriangle className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-rose-300">
                Erreurs enregistrées
              </p>
              <p className="font-display text-3xl font-black text-[var(--text)]" data-testid="errors-total">
                {total}
              </p>
            </div>
          </div>
          <div className="sm:ml-auto flex flex-wrap gap-2">
            {HOUR_RANGES.map((r) => (
              <button
                key={r.v}
                onClick={() => setHours(r.v)}
                data-testid={`range-${r.v}`}
                className={`rounded-full px-4 py-2 text-xs font-bold border-2 transition-all active:scale-95 ${
                  hours === r.v
                    ? "bg-rose-600 text-white border-rose-600"
                    : "bg-[var(--surface)] text-rose-300 border-rose-500/40 hover:border-rose-500"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {/* Top messages */}
        {top.length > 0 && (
          <div className="bg-[var(--surface)] rounded-xl border border-[var(--border-soft)] p-5 shadow-sm">
            <h2 className="font-bold text-[var(--text)] mb-3">Top erreurs récurrentes</h2>
            <div className="space-y-2">
              {top.map((t, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 border border-rose-100 rounded-2xl px-4 py-3 bg-rose-500/10/40"
                  data-testid={`top-error-${i}`}
                >
                  <span className="w-7 h-7 rounded-full bg-rose-500/100 text-white text-xs font-black grid place-items-center shrink-0">
                    {t.count}
                  </span>
                  <p className="text-sm font-medium text-zinc-200 truncate flex-1" title={t.message}>
                    {t.message}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent items list */}
        <div className="bg-[var(--surface)] rounded-xl border border-[var(--border-soft)] p-5 shadow-sm">
          <h2 className="font-bold text-[var(--text)] mb-3">
            Dernières erreurs · <span className="text-zinc-400 font-normal">{items.length} affichées</span>
          </h2>
          {items.length === 0 ? (
            <div className="text-center py-10">
              <div className="w-14 h-14 rounded-full bg-emerald-500/15 grid place-items-center mx-auto mb-3">
                <span className="text-2xl">✨</span>
              </div>
              <p className="text-sm font-bold text-[var(--text)]">Aucune erreur sur cette période</p>
              <p className="text-xs text-zinc-400 mt-1">Votre app tourne proprement. Continuez comme ça.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((err, i) => (
                <button
                  key={i}
                  onClick={() => setSelected(err)}
                  data-testid={`error-item-${i}`}
                  className="w-full text-left border border-rose-100 rounded-2xl p-3.5 hover:border-rose-300 hover:bg-rose-500/10/40 transition-all active:scale-[0.99]"
                >
                  <p className="text-sm font-semibold text-[var(--text)] truncate">{err.message}</p>
                  <div className="flex items-center gap-3 mt-1.5 text-[11px] text-zinc-400 flex-wrap">
                    {err.url && (
                      <span className="inline-flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" />
                        <span className="truncate max-w-[280px]">{err.url.replace(/^https?:\/\/[^/]+/, "")}</span>
                      </span>
                    )}
                    {err.user_id ? (
                      <span className="inline-flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {err.user_id.slice(-8)}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-zinc-500">
                        <User className="w-3 h-3" /> anonyme
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1 ml-auto">
                      <Clock className="w-3 h-3" />
                      {err.created_at?.slice(0, 16).replace("T", " ")}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Detail drawer (bottom sheet on mobile, side on desktop) */}
      {selected && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm flex items-end sm:items-center sm:justify-center"
          onClick={() => setSelected(null)}
          data-testid="error-detail-overlay"
        >
          <div
            className="bg-[var(--surface)] border border-[var(--border)] w-full sm:max-w-2xl sm:rounded-3xl rounded-t-3xl max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-[var(--surface)] border-b border-[var(--border-soft)] px-5 py-4 flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-widest text-rose-400">Détail de l'erreur</p>
              <button
                onClick={() => setSelected(null)}
                className="text-xl text-zinc-500 hover:text-[var(--text)] px-2"
                data-testid="close-detail"
              >
                ×
              </button>
            </div>
            <div className="px-5 py-5 space-y-4 text-sm">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1">Message</p>
                <p className="font-bold text-[var(--text)] break-words">{selected.message}</p>
              </div>
              {selected.url && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1">URL</p>
                  <p className="text-zinc-200 break-all text-xs">{selected.url}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1">Date</p>
                  <p className="text-zinc-200 text-xs">{selected.created_at?.replace("T", " ").slice(0, 19)}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1">User</p>
                  <p className="text-zinc-200 text-xs">{selected.user_id || "anonyme"}</p>
                </div>
              </div>
              {selected.user_agent && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1">User-Agent</p>
                  <p className="text-zinc-400 text-[11px] break-all">{selected.user_agent}</p>
                </div>
              )}
              {selected.stack && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1">Stack</p>
                  <pre className="text-[11px] text-rose-300 bg-rose-500/10 border border-rose-100 rounded-xl p-3 overflow-auto whitespace-pre-wrap max-h-64">
                    {selected.stack}
                  </pre>
                </div>
              )}
              {selected.component_stack && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1">Component stack</p>
                  <pre className="text-[11px] text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 overflow-auto whitespace-pre-wrap max-h-48">
                    {selected.component_stack}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
